import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Plus, Feather, Trash2, Sparkles, LogOut, Crown, Image as ImageIcon, ShieldCheck, Settings as SettingsIcon, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogTrigger, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { listNovels, createNovel, deleteNovel, billingMe, absUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import HelpMenu from "@/components/HelpMenu";
import BackgroundPicker, { resolveBgUrl } from "@/components/BackgroundPicker";
import { useGate } from "@/lib/gate";
import { useTranslation } from "@/lib/i18n";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

const COVERS = ["/img/covers/cover-1.jpg", "/img/covers/cover-2.jpg"];

export default function Dashboard() {
    const nav = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const gate = useGate();
    const { t } = useTranslation();
    const [novels, setNovels] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [me, setMe] = useState(null);
    const [form, setForm] = useState({ title: "", genre: "", synopsis: "", pov: "", tone: "", daily_goal: 500 });
    const [bgPickerOpen, setBgPickerOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [novelsRes, meRes] = await Promise.all([listNovels(), billingMe().catch(() => null)]);
            setNovels(novelsRes);
            setMe(meRes);
        }
        catch { toast.error("Failed to load novels"); }
        setLoading(false);
    }
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (location.state?.reactivated) {
            toast.success("Welcome back — your account was restored");
            nav(location.pathname, { replace: true, state: {} });
        }
    }, [location, nav]);

    async function onCreate(e) {
        e.preventDefault();
        if (!form.title.trim()) { toast.error("Title required"); return; }
        try {
            const n = await createNovel({ ...form, daily_goal: Number(form.daily_goal) || 500 });
            toast.success(`Created "${n.title}"`);
            setOpen(false);
            setForm({ title: "", genre: "", synopsis: "", pov: "", tone: "", daily_goal: 500 });
            nav(`/novel/${n.id}`);
        } catch (e) {
            if (e?.response?.status === 402) {
                toast.error(e.response.data?.detail || "Upgrade to create more novels");
                setOpen(false);
                nav("/pricing");
            } else {
                toast.error("Failed to create");
            }
        }
    }

    async function onDelete(id, e) {
        e.stopPropagation();
        if (!confirm("Delete this novel and all its chapters?")) return;
        try { await deleteNovel(id); toast.success("Deleted"); load(); }
        catch { toast.error("Failed"); }
    }

    const userInitials = (user?.name || user?.email || "U").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();

    return (
        <div
            className="min-h-screen relative"
            style={{
                backgroundImage: `url(${resolveBgUrl(user)})`,
                backgroundSize: "cover",
                backgroundPosition: "center top",
                backgroundAttachment: "fixed",
            }}
        >
            {/* Dark overlay for legibility against the painted scene */}
            <div className="fixed inset-0 bg-gradient-to-b from-black/50 via-black/45 to-black/80 pointer-events-none" />

            {/* Hero */}
            <div className="relative h-[460px] w-full">
                <div className="absolute inset-x-0 top-0 px-8 lg:px-16 pt-10 flex items-center justify-between text-white">
                    <div className="flex flex-col items-start">
                        <div className="flex items-center gap-2.5">
                            <Feather className="w-5 h-5" />
                            <span className="font-heading text-2xl tracking-tight">Day Dreamer</span>
                            <span className="ml-1 text-[11px] uppercase tracking-[0.25em] text-white/60 hidden sm:inline">by H.Tokoki</span>
                        </div>
                        <button
                            data-testid="change-bg-button"
                            onClick={() => setBgPickerOpen(true)}
                            className="mt-1 ml-7 text-[10px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors flex items-center gap-1.5"
                        >
                            <ImageIcon className="w-3 h-3" /> {t("dash.changeImage")}
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full pl-2 pr-1 py-1 hover:bg-white/10 transition-colors">
                                    <span className="text-xs uppercase tracking-[0.2em] hidden sm:inline text-white/90">{user?.name || user?.email}</span>
                                    <Avatar className="w-8 h-8 border border-white/40">
                                        {user?.picture && <AvatarImage src={user.picture} />}
                                        <AvatarFallback className="text-xs">{userInitials}</AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>
                                    <div className="text-sm font-medium flex items-center gap-1.5">
                                        {user?.name}
                                        {user?.is_admin && (
                                            <span data-testid="admin-badge" className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
                                                {t("dash.admin")}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">{user?.email}</div>
                                    {me && me.billing_enabled && (
                                        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
                                            <Crown className={`w-3 h-3 ${me.plan === "thoughts" ? "text-muted-foreground" : "text-primary"}`} />
                                            <span data-testid="plan-badge">{me.plan_label}</span>
                                            {me.plan_until && (
                                                <span className="text-muted-foreground">· {new Date(me.plan_until).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    )}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {user?.is_admin && (
                                    <DropdownMenuItem data-testid="admin-inbox-menu-item" onClick={() => nav("/admin")}>
                                        <ShieldCheck className="w-4 h-4" /> {t("dash.adminInbox")}
                                    </DropdownMenuItem>
                                )}
                                {me?.billing_enabled && !user?.is_admin && (
                                    <DropdownMenuItem data-testid="upgrade-menu-item" onClick={() => nav("/pricing")}>
                                        <Crown className="w-4 h-4" />
                                        {me?.limits?.paid ? t("dash.managePlan") : t("dash.upgrade")}
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem data-testid="logout-menu-item" onClick={logout}>
                                    <LogOut className="w-4 h-4" /> {t("nav.signOut")}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <button
                            data-testid="settings-gear"
                            aria-label="Settings"
                            onClick={() => nav("/settings")}
                            className="w-9 h-9 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/30 transition-colors"
                        >
                            <SettingsIcon className="w-4 h-4" />
                        </button>
                        <HelpMenu triggerClassName="border border-white/30 w-9 h-9 !text-white bg-white/10 hover:bg-white/20" />
                    </div>
                </div>
                <div className="absolute bottom-14 px-8 lg:px-16 max-w-3xl text-white">
                    <div className="text-xs uppercase tracking-[0.25em] text-white/70 mb-3">{t("dash.eyebrow")}</div>
                    <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-white">
                        {t("dash.heroLine1")}<br /><em className="text-white/95">{t("dash.heroLine2")}</em> {t("dash.heroLine3")}
                    </h1>
                    <p className="mt-4 text-sm sm:text-base text-white/80 max-w-xl">
                        {t("dash.heroSubtitle")}
                    </p>
                </div>
            </div>

            <div className="relative px-8 lg:px-16 pb-20">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-white/70 mb-2">{t("dash.yourManuscripts")}</div>
                        <h2 className="font-heading text-3xl text-white">{novels.length} {novels.length === 1 ? t("dash.novel") : t("dash.novels")}</h2>
                    </div>
                    <Dialog open={open} onOpenChange={(o) => {
                        if (o && !gate.canCreateNovel(novels.length)) {
                            gate.openPaywall("novels");
                            return;
                        }
                        setOpen(o);
                    }}>
                        <DialogTrigger asChild>
                            {gate.canCreateNovel(novels.length) ? (
                                <Button
                                    data-testid="new-novel-button"
                                    aria-label={t("dash.newNovel")}
                                    title={t("dash.newNovel")}
                                    className="w-14 h-14 p-0 rounded-full shadow-lg hover:shadow-xl transition-shadow"
                                >
                                    <Plus className="w-6 h-6" />
                                </Button>
                            ) : (
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                data-testid="new-novel-button-locked"
                                                aria-label={t("dash.newNovelLocked")}
                                                onClick={(e) => { e.preventDefault(); gate.openPaywall("novels"); }}
                                                className="w-14 h-14 p-0 rounded-full shadow-lg hover:shadow-xl transition-shadow relative opacity-70 hover:opacity-90"
                                            >
                                                <Plus className="w-6 h-6" />
                                                <Lock className="w-3.5 h-3.5 absolute -top-1 -right-1 bg-background rounded-full p-0.5 border border-border shadow-sm text-primary" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="text-xs">
                                            {t("dash.limitReached")}
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-lg">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-2xl font-normal">{t("dash.begin")}</DialogTitle>
                                <DialogDescription>{t("dash.beginDesc")}</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={onCreate} className="space-y-4">
                                <div>
                                    <Label htmlFor="title" className="ui-label">{t("dash.field.title")}</Label>
                                    <Input id="title" data-testid="novel-title-input" value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        placeholder={t("dash.ph.title")} className="mt-1.5" autoFocus />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="genre" className="ui-label">{t("dash.field.genre")}</Label>
                                        <Input id="genre" data-testid="novel-genre-input" value={form.genre}
                                            onChange={(e) => setForm({ ...form, genre: e.target.value })}
                                            placeholder={t("dash.ph.genre")} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label htmlFor="pov" className="ui-label">{t("dash.field.pov")}</Label>
                                        <Input id="pov" data-testid="novel-pov-input" value={form.pov}
                                            onChange={(e) => setForm({ ...form, pov: e.target.value })}
                                            placeholder={t("dash.ph.pov")} className="mt-1.5" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="tone" className="ui-label">{t("dash.field.tone")}</Label>
                                        <Input id="tone" data-testid="novel-tone-input" value={form.tone}
                                            onChange={(e) => setForm({ ...form, tone: e.target.value })}
                                            placeholder={t("dash.ph.tone")} className="mt-1.5" />
                                    </div>
                                    <div>
                                        <Label htmlFor="daily_goal" className="ui-label">{t("dash.field.dailyGoal")}</Label>
                                        <Input id="daily_goal" type="number" data-testid="novel-goal-input" value={form.daily_goal}
                                            onChange={(e) => setForm({ ...form, daily_goal: e.target.value })}
                                            min={50} step={50} className="mt-1.5" />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="synopsis" className="ui-label">{t("dash.field.synopsis")}</Label>
                                    <Textarea id="synopsis" data-testid="novel-synopsis-input" value={form.synopsis}
                                        onChange={(e) => setForm({ ...form, synopsis: e.target.value })}
                                        rows={4} placeholder={t("dash.ph.synopsis")}
                                        className="mt-1.5" />
                                </div>
                                <DialogFooter>
                                    <Button type="submit" data-testid="create-novel-submit" className="rounded-full">
                                        <Sparkles className="w-4 h-4" /> {t("dash.createNovel")}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {loading ? (
                    <div className="text-muted-foreground">{t("common.loading")}</div>
                ) : novels.length === 0 ? (
                    <Card className="p-16 text-center border-dashed">
                        <BookOpen className="w-10 h-10 mx-auto text-muted-foreground/60 mb-4" />
                        <h3 className="font-heading text-2xl mb-2">{t("dash.noManuscripts")}</h3>
                        <p className="text-sm text-muted-foreground mb-6">{t("dash.startFirst")}</p>
                        <Button data-testid="new-novel-empty-button" onClick={() => {
                            if (!gate.canCreateNovel(novels.length)) { gate.openPaywall("novels"); return; }
                            setOpen(true);
                        }} className="rounded-full">
                            <Plus className="w-4 h-4" /> {t("dash.startWriting")}
                        </Button>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {novels.map((n, i) => (
                            <Card
                                key={n.id}
                                data-testid={`novel-card-${n.id}`}
                                onClick={() => nav(`/novel/${n.id}`)}
                                className="group cursor-pointer overflow-hidden border-border/70 hover:shadow-md transition-shadow duration-300 bg-card"
                            >
                                <div className="aspect-[3/2] relative overflow-hidden">
                                    <img src={n.cover_url ? absUrl(n.cover_url) : COVERS[i % COVERS.length]} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                                    <div className="absolute bottom-3 left-4 right-4 text-white">
                                        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">{n.genre || t("dash.untitledGenre")}</div>
                                        <div className="font-heading text-2xl leading-tight mt-1 line-clamp-2">{n.title}</div>
                                    </div>
                                </div>
                                <div className="p-4">
                                    <p className="text-sm text-muted-foreground line-clamp-2 min-h-[40px]">
                                        {n.synopsis || t("dash.noSynopsis")}
                                    </p>
                                    <div className="mt-3 flex items-center justify-between">
                                        <span className="ui-label">{new Date(n.updated_at).toLocaleDateString()}</span>
                                        <Button
                                            data-testid={`delete-novel-${n.id}`}
                                            variant="ghost" size="sm"
                                            onClick={(e) => onDelete(n.id, e)}
                                            className="text-muted-foreground hover:text-destructive">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            <BackgroundPicker open={bgPickerOpen} onClose={() => setBgPickerOpen(false)} />
        </div>
    );
}
